import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Circle, Filter, Target, Loader2, Save } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface SegmentationToolProps {
    client: CJMSClient;
}

const SEGMENTS = [
    { id: 'high_value', label: 'High Value', desc: 'Top 20% of clients by revenue', color: 'bg-emerald-500' },
    { id: 'growth_potential', label: 'Growth Potential', desc: 'Ready for upsell or expansion', color: 'bg-blue-500' },
    { id: 'at_risk', label: 'At Risk', desc: 'Declining engagement or satisfaction', color: 'bg-amber-500' },
    { id: 'advocacy', label: 'Advocacy Ready', desc: 'Strong candidates for referral programs', color: 'bg-purple-500' },
    { id: 'new', label: 'New/Acclimating', desc: 'Still in onboarding phase', color: 'bg-cyan-500' },
];

export const SegmentationTool: React.FC<SegmentationToolProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Growth' || client.currentStage === 'Advocacy';
    const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    if (!isEligible) return null;

    const handleAssign = async (segmentId: string) => {
        setSelectedSegment(segmentId);
        feedbackBus.emit('activity_completed', {
            activityLabel: `Client segmented as: ${SEGMENTS.find(s => s.id === segmentId)?.label}`,
            clientEmail: client.email,
        });
    };

    const handleSave = async () => {
        setStatus('saving');
        await new Promise(r => setTimeout(r, 500));
        setStatus('saved');
        setTimeout(() => setStatus('idle'), 2000);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <Users className="w-4 h-4 text-pink-600" />
                <h3 className="text-sm font-bold text-slate-800">Segmentation Tool</h3>
                <span className="text-[10px] text-slate-400 ml-auto">
                    {client.company || client.name}
                </span>
            </div>
            <div className="p-4 space-y-4">
                <div className="flex items-center gap-2 p-2 bg-pink-50 rounded-lg border border-pink-100">
                    <Filter className="w-3 h-3 text-pink-600" />
                    <p className="text-[10px] text-pink-700">Assign this client to a segment for better targeting</p>
                </div>
                <div className="grid grid-cols-1 gap-2">
                    {SEGMENTS.map(seg => {
                        const isSelected = selectedSegment === seg.id;
                        return (
                            <button
                                key={seg.id}
                                onClick={() => handleAssign(seg.id)}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                                    isSelected
                                        ? 'border-pink-400 bg-pink-50 shadow-sm'
                                        : 'border-slate-100 bg-white hover:border-pink-200'
                                }`}
                            >
                                <div className={`w-3 h-3 rounded-full shrink-0 ${seg.color}`} />
                                <div className="flex-1 min-w-0">
                                    <span className={`text-xs font-semibold ${isSelected ? 'text-pink-700' : 'text-slate-700'}`}>
                                        {seg.label}
                                    </span>
                                    <p className="text-[10px] text-slate-500">{seg.desc}</p>
                                </div>
                                {isSelected && <CheckCircle className="w-4 h-4 text-pink-600 shrink-0" />}
                            </button>
                        );
                    })}
                </div>
                {selectedSegment && (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Segment Notes</label>
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Why was this segment chosen?"
                                className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-16 focus:ring-1 focus:ring-pink-400 outline-none"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={status === 'saving'}
                                className="px-3 py-1.5 text-[11px] font-medium bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                                {status === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save Assignment
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
